import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  matchPath,
  useLocation,
} from "react-router-dom";

import { HelmetProvider } from "react-helmet-async";
import { startIdleTracking } from "./utils/idleTracker";

import PublicLayout from "./layouts/PublicLayout";
import PrivateRoute from "./routes/PrivateRoute";
import LandingPage from "./pages/LandingPage";
import ScrapLandingPage from "./pages/ScrapLandingPage";

import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import LoginWithOtp from "./pages/auth/LoginWithOtp";
import RequestOtp from "./pages/auth/RequestOtp";
import ResetPassword from "./pages/auth/ResetPassword";
import BuyerLayout from "./layouts/BuyerLayout";
import SellerLayout from "./layouts/SellerLayout";
// import DashBoard from "./pages/buyer/DashBoard";
import DashBoard from "./pages/buyer/Dashboard/Dashboard";
import { getFcmToken, getToken, getUser } from "./utils/token";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "./features/auth/authSlice";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { setNavigate } from "./utils/navigationHelper";
import ProductList from "./pages/buyer/ProductsList";
import Dashboard from "./pages/seller/Dashboard";
import Products from "./pages/seller/Products";
import UploadKyc from "./pages/seller/UploadKyc";
import UploadKycForm from "./pages/seller/UploadKycForm";
import AddNewProduct from "./pages/seller/AddNewProduct";
import ViewTrash from "./pages/seller/ViewTrash";
import MetalPrice from "./pages/seller/MetalPrice";
import ManageStock from "./pages/seller/ManageStock";
import SellerBids from "./pages/seller/SellerBids";
import ViewOrders from "./pages/seller/ViewOrders";
import Notifications from "./pages/seller/Notifications";

import BuyScrapPage from "./pages/BuyScrapPage";
import SellScrapPage from "./pages/SellScrapPage";
import SellerProfile from "./pages/seller/SellerProfile";
import SellerEditProfile from "./pages/seller/SellerEditProfile";
import ChangePassword from "./pages/seller/ChangePassword";
import AddressFormPage from "./pages/seller/AddressFormPage";
import AboutUs from "./pages/AboutUsPage";
import Notification from "./pages/buyer/Notification";
import MyBids from "./pages/buyer/MyBids";
import ContactUs from "./pages/buyer/ContactUs";
import MyOrders from "./pages/buyer/MyOrders";
import OrderDetails from "./pages/buyer/OrderDetails";
import OrderTrack from "./pages/buyer/OrderTrack";
import OrderSummary from "./pages/buyer/OrderSummary";
import PaymentStatusPage from "./pages/buyer/PaymentStatusPage";
import OrderConfirmationPage from "./components/buyer/OrderConfirmationPage";
import Market from "./pages/buyer/Market";
import HowItWorks from "./pages/HowItWorksPage";
import SiteMap from "./pages/buyer/SiteMap";
import MetalsMaterialsPage from "./pages/buyer/MetalsMaterialsPage";
import FerrousMetals from "./pages/buyer/FerrousMetalsPage";
import NonFerrousMetals from "./pages/buyer/NonFerrousMetalsPage";
import SpecializedScrapPage from "./pages/buyer/SpecializedScrapPage";
import SustainabilityImpactPage from "./pages/buyer/SustainabilityImpactPage";
import OurCommitmentPage from "./pages/buyer/OurCommitmentPage";
import ImpactReportingPage from "./pages/buyer/ImpactReportingPage";
import PartnershipsPage from "./pages/buyer/PartnershipsPage";
import ResourcesOverviewPage from "./pages/buyer/ResourcesOverviewPage";
import BlogPage from "./pages/buyer/BlogPage";
import FAQsPage from "./pages/buyer/FAQsPage";
import GlossaryPage from "./pages/buyer/GlossaryPage";

import LegalCompliancePage from "./pages/buyer/LegalCompliancePage";
import TermsOfServicePage from "./pages/buyer/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/buyer/PrivacyPolicyPage";
import AntiTheftVerificationPage from "./pages/buyer/AntiTheftVerificationPage";
import RegulatoryInformationPage from "./pages/buyer/RegulatoryInformationPage";
import CybersecurityDataProtectionPage from "./pages/buyer/CybersecurityDataProtectionPage";
import CompanyOverviewPage from "./pages/buyer/CompanyOverviewPage";
import CareersPage from "./pages/buyer/CareersPage";
import PressMediaPage from "./pages/buyer/PressMediaPage";

import Blog from "./pages/buyer/Blog";
import BlogDetails from "./pages/buyer/BlogDetail";

import MyRequestedBids from "./pages/buyer/MyRequestedBids";
import RequestedBids from "./pages/seller/RequestedBids";
import RequestedBidOrders from "./pages/seller/RequestedBidOrders";
import Certificates from "./pages/seller/Certificates";
import CertificatesForm from "./pages/seller/CertificatesForm";
import {
  fetchUserProfile,
  fetchUserAddresses,
} from "./features/user/userSlice";
import ProtectedRouteWithVerification from "./components/seller/ProtectedRouteWithVerification";
import StripeCallbackHandler from "./components/seller/StripeCallbackHandler";
import NotificationHandler from "./firebase/NotificationHandler";
import ProfilePage from "./components/buyer/ProfileOverview/ProfilePage";
import DynamicPageWrapper from "./pages/buyer/DynamicPageWrapper";
import MarketPage from "./pages/buyer/MarketPage";
import Error404Page from "./pages/Error404Page";
import ProductDetailPage from "./pages/buyer/ProductDetailPage";
import BuyerChatPage from "./pages/buyer/BuyerChatPage";
import SellerChatPage from "./pages/seller/SellerChatPage";
import KycVerificationPage from "./pages/buyer/KycVerificationPage";
import AddProduct from "./pages/seller/AddProduct/AddProduct";
import ScrollToTop from "./utils/ScrollToTop";
import ShippingModePage from "./pages/buyer/ShippingPage";
import ShippingTabsCard from "./pages/buyer/Shipping/ShippingTabsCard";
import ShippingModule from "./pages/buyer/shippingModule/ShippingModule";

import BuyerOfferForm from "./components/buyer/BuyerOfferForm";
import AddBuyerProduct from "./pages/buyer/AddProduct/AddOffer";
import AddOffer from "./pages/buyer/AddProduct/AddOffer";
import MyOffers from "./pages/buyer/MyOffers";
import ReceivedOffers from "./pages/seller/ReceivedOffers";
import MyTransactions from "./pages/buyer/MyTransactions";
import TransactionDetails from "./pages/buyer/TransactionsDetails";
import RangeRequests from "./pages/buyer/RangeRequests";
import RangeRequested from "./pages/seller/RangeRequested";
import SellerProductDetailPage from "./pages/seller/SellerProductDetailPage";
import SellerOrderDetail from "./pages/seller/SellerOrderDetail";
import SellerOfferDetail from "./pages/seller/SellerOfferDetail";
import SelectRole from "./pages/auth/SelectRole";
import VerificationList from "./pages/seller/VerificationList";
import VerificationForm from "./pages/seller/VerificationForm_backup";
import CookieConsent from "./components/common/CookieConsent";
import KycBanner from "./components/common/KycBanner";
import PricingPage from "./pages/buyer/Pricing";
import SessionExpiredModal from "./components/SessionExpiredModal";
import Billing from "./pages/buyer/Billing";
import BillingHistory from "./pages/buyer/BillingHistory";

function AppRoutes() {
  // console.log("%c [APP RELOADED] " + new Date().toLocaleTimeString(), "background: red; color: white; font-size: 20px; padding: 10px; border-radius: 5px;");
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();

  const fcm_token = getFcmToken();
  // console.log("FCM Token on App load:", fcm_token);

  // Redirect HTTP to HTTPS (except for localhost) - Works reliably on all browsers including iPhone
  useEffect(() => {
    if (
      window.location.protocol === "http:" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      const httpsUrl = window.location.href.replace("http://", "https://");
      window.location.href = httpsUrl;
    }
  }, []);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    // console.log("Auth token in Redux:", token);
    // console.log("Auth user in Redux:", user);
    // console.log("Token in localStorage:", getToken());
  }, [token, user]);

  useEffect(() => {
    const storedToken = getToken();
    const MIN_SPLASH_TIME = 1500; // 1.5 seconds
    const start = Date.now();

    const finishLoading = () => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(MIN_SPLASH_TIME - elapsed, 0);
      setTimeout(() => setLoading(false), remaining);
    };

    if (storedToken) {
      dispatch(fetchUserProfile())
        .unwrap()
        .then(async (userData) => {
          let addressCount = 0;
          if (userData.role === "seller") {
            try {
              const addressRes = await dispatch(fetchUserAddresses()).unwrap();
              // Depending on API structure, addresses might be in .data or the root
              const addressList = addressRes?.data || addressRes;
              addressCount = Array.isArray(addressList)
                ? addressList.length
                : 0;
            } catch (addrErr) {
              console.error("Error fetching addresses on load:", addrErr);
            }
          }

          dispatch(
            setCredentials({
              token: storedToken,
              user: { ...userData, addresses_count: addressCount },
              role: userData.role,
            }),
          );
          finishLoading();
        })
        .catch((err) => {
          console.error("Error fetching user after redirect:", err);
          // Optionally clear token or show toast
          finishLoading();
        });
    } else {
      finishLoading();
    }
  }, [dispatch]);

  useEffect(() => {
    const user = getUser();
    if (user?.role === "seller") {
      dispatch(fetchUserProfile());
    }
  }, [dispatch]);

  useEffect(() => {
    console.log("Idle tracking started");
    startIdleTracking(); // updates last_activity on user activity
    if (typeof window.hasExpired === "undefined") window.hasExpired = false;

    const interval = setInterval(() => {
      const lastActivity = parseInt(
        localStorage.getItem("last_activity") || "0",
      );
      const now = Date.now();
      const idleLimit = 5 * 60 * 1000;

      if (!window.hasExpired && now - lastActivity > idleLimit) {
        console.log("User idle → triggering sessionExpired modal");
        window.dispatchEvent(new Event("sessionExpired"));
        window.hasExpired = true;
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const publicPaths = React.useMemo(
    () => [
      "/",
      "/home",
      "/select-role",
      "/login",
      "/register",
      "/forgot-password",
      "/reset-password",
      "/login-otp",
      "/request-otp",
      "/buy",
      "/sell",
      "/market",
      "/how-it-works",
      "/pricing",
      "/metals-materials",
      "/metals-materials/ferrous",
      "/metals-materials/non-ferrous",
      "/metals-materials/specialized-scrap",
      "/sustainability",
      "/sustainability/our-commitment",
      "/sustainability/impact-reporting",
      "/sustainability/partnerships",
      "/resources",
      "/resources/blog",
      "/resources/faqs",
      "/resources/glossary",
      "/legal",
      "/legal/terms-of-service",
      "/legal/privacy-policy",
      "/legal/anti-theft-policy",
      "/legal/regulations",
      "/legal/cybersecurity",
      "/company",
      "/company/careers",
      "/company/press",
      "/sitemap",
      "/about-us",
      "/contact-us",
      "/legal",
      "/sitemap",
      "/blog",
      "/blog/:slug",
      "/pages/slug/:slug",
    ],
    [],
  );

  // Handle buyer contact verification redirects
  useEffect(() => {
    // Check email + phone verified (matching KycBanner pattern)
    const isEmailVerified =
      user?.email_verified_at !== null && user?.email_verified_at !== undefined;
    const isPhoneVerified =
      user?.phone_verified_at !== null && user?.phone_verified_at !== undefined;
    const isContactVerified = isEmailVerified && isPhoneVerified;

    if (!loading && token && user?.role === "buyer" && !isContactVerified) {
      const isPublicPath = publicPaths.some((pattern) => {
        const isParameterized = pattern.includes(":");
        return matchPath(
          { path: pattern, end: !isParameterized },
          location.pathname,
        );
      });

      const isAllowedForUnverified = ["/kyc-verification"].some((pattern) => {
        const isParameterized = pattern.includes(":");
        return matchPath(
          { path: pattern, end: !isParameterized },
          location.pathname,
        );
      });

      if (!isPublicPath && !isAllowedForUnverified) {
        // Redirect buyers who haven't verified email+phone to market
        navigate("/market");
      }
    }
  }, [token, user, navigate, loading, location.pathname, publicPaths]);

  // if (loading) {
  //   console.log("%c [App.jsx] Blocking render for hydration...", "background: orange; color: black;");
  //   return (
  //     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
  //       <div className="spinner-border text-primary" role="status"></div>
  //       <span>Loading your session...</span>
  //     </div>
  //   );
  // }

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route index element={<LandingPage />} />
          <Route path="/" element={<ScrapLandingPage />} />
          <Route path="/home" element={<LandingPage />} />

          <Route path="/buy" element={<BuyScrapPage />} />
          <Route path="/sell" element={<SellScrapPage />} />

          {/* <Route path="/select-role" element={<SelectRole />} /> */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/login-otp" element={<LoginWithOtp />} />
          <Route path="/request-otp" element={<RequestOtp />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/payment-success" element={<PaymentStatusPage />} />
          <Route path="/payment-cancel" element={<PaymentStatusPage />} />
          <Route path="/payment-status" element={<PaymentStatusPage />} />
          <Route path="/market" element={<Market />} />
          <Route path="/new-market" element={<MarketPage />} />

          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/metals-materials" element={<MetalsMaterialsPage />} />
          <Route path="/metals-materials/ferrous" element={<FerrousMetals />} />
          <Route
            path="/metals-materials/non-ferrous"
            element={<NonFerrousMetals />}
          />
          <Route
            path="/metals-materials/specialized-scrap"
            element={<SpecializedScrapPage />}
          />
          <Route
            path="/sustainability"
            element={<SustainabilityImpactPage />}
          />
          <Route
            path="/sustainability/our-commitment"
            element={<OurCommitmentPage />}
          />
          <Route
            path="/sustainability/impact-reporting"
            element={<ImpactReportingPage />}
          />
          <Route
            path="/sustainability/partnerships"
            element={<PartnershipsPage />}
          />
          <Route path="/resources" element={<ResourcesOverviewPage />} />
          <Route path="/resources/blog" element={<BlogPage />} />
          <Route path="/resources/faqs" element={<FAQsPage />} />
          <Route path="/resources/glossary" element={<GlossaryPage />} />

          <Route path="/legal" element={<LegalCompliancePage />} />
          <Route
            path="/legal/terms-of-service"
            element={<TermsOfServicePage />}
          />
          <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route
            path="/legal/anti-theft-policy"
            element={<AntiTheftVerificationPage />}
          />
          <Route
            path="/legal/regulations"
            element={<RegulatoryInformationPage />}
          />
          <Route
            path="/legal/cybersecurity"
            element={<CybersecurityDataProtectionPage />}
          />
          <Route path="/company" element={<CompanyOverviewPage />} />
          <Route path="/company/careers" element={<CareersPage />} />
          <Route path="/company/press" element={<PressMediaPage />} />

          <Route path="/sitemap" element={<SiteMap />} />
          <Route path="/stripe/callback" element={<StripeCallbackHandler />} />

          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetails />} />
          <Route path="/pages/slug/:slug" element={<DynamicPageWrapper />} />
          <Route path="/contact-us" element={<ContactUs />} />
        </Route>

        {/* Seller protected routes */}
        <Route
          path="/seller"
          element={<PrivateRoute allowedRoles={["seller"]} />}
        >
          <Route element={<ProtectedRouteWithVerification />}>
            <Route element={<SellerLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="products" element={<Products />} />
              <Route path="view-orders" element={<ViewOrders />} />
              <Route path="order-detail/:id" element={<SellerOrderDetail />} />
              <Route path="upload-kyc" element={<UploadKyc />} />
              <Route path="upload-kyc-form" element={<UploadKycForm />} />
              <Route path="certificate" element={<Certificates />} />
              <Route path="certificate-form" element={<CertificatesForm />} />
              <Route path="verification-list" element={<VerificationList />} />
              <Route path="verification-form" element={<VerificationForm />} />
              <Route path="add-new-product" element={<AddNewProduct />} />
              <Route path="add-new-product/:id" element={<AddNewProduct />} />
              <Route path="add-product" element={<AddProduct />} />
              <Route path="add-product/:id" element={<AddProduct />} />
              <Route
                path="product-detail/:id"
                element={<SellerProductDetailPage />}
              />
              <Route path="view-trash" element={<ViewTrash />} />
              <Route path="metal-price" element={<MetalPrice />} />
              <Route path="manage-stock" element={<ManageStock />} />
              <Route path="seller-bids" element={<SellerBids />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="profile" element={<SellerProfile />} />
              <Route path="edit-profile" element={<SellerEditProfile />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="address/:id?" element={<AddressFormPage />} />
              <Route path="requested-bids" element={<RequestedBids />} />
              <Route path="received-offers" element={<ReceivedOffers />} />
              <Route path="offers-detail/:id" element={<SellerOfferDetail />} />
              <Route path="range-requested" element={<RangeRequested />} />
              <Route path="chat" element={<SellerChatPage />} />
              <Route
                path="requested-bid-orders"
                element={<RequestedBidOrders />}
              />
            </Route>
          </Route>
        </Route>

        {/* Buyer protected routes */}
        <Route element={<PrivateRoute allowedRoles={["buyer"]} />}>
          <Route element={<BuyerLayout />}>
            <Route index element={<DashBoard />} />
            <Route path="dashboard" element={<DashBoard />} />
            <Route path="products" element={<ProductList />} />
            <Route path="product/:id" element={<ProductDetailPage />} />
            <Route path="kyc-verification" element={<KycVerificationPage />} />
            <Route path="profile" element={<ProfilePage />} />

            {/* <Route path="profile-overview" element={<ProfilePage />} /> */}
            <Route path="notifications" element={<Notification />} />
            <Route path="my-bids" element={<MyBids />} />

            <Route path="my-orders" element={<MyOrders />} />
            <Route path="order-track/:id" element={<OrderTrack />} />
            <Route path="order-details/:id" element={<OrderDetails />} />
            <Route path="order-summary/:id" element={<OrderSummary />} />
            <Route path="my-requested-bids" element={<MyRequestedBids />} />

            <Route path="shipping-mode/:id" element={<ShippingModule />} />
            <Route
              path="order-confirmation"
              element={<OrderConfirmationPage />}
            />
            <Route path="chat" element={<BuyerChatPage />} />
            <Route path="/create-offer" element={<AddOffer />} />
            <Route path="/add-offer/:id" element={<AddOffer />} />
            <Route path="/my-offers" element={<MyOffers />} />
            <Route path="/my-transactions" element={<MyTransactions />} />
            <Route
              path="/transaction-details/:id"
              element={<TransactionDetails />}
            />
            <Route path="/range-requests" element={<RangeRequests />} />
            <Route
              path="/settings/billing"
              element={<Billing user={user} />}
            />
            <Route
              path="/settings/billing/history"
              element={<BillingHistory />}
            />
          </Route>
        </Route>
        {/* 404 fallback */}
        <Route path="*" element={<Error404Page />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AppRoutes />
        <ToastContainer position="top-right" autoClose={3000} />
        <NotificationHandler />
        {/* {/* <CookieConsent /> */}
        <SessionExpiredModal />
      </Router>
    </HelmetProvider>
  );
}

export default App;
